import { useEffect, useMemo, useState } from 'react';
import { Button, Card, CardBody, Select, SelectItem } from '@nextui-org/react';
import { toast } from 'react-toastify';
import useUserStore from '../../../../common/src/stores/useUserStore';
import useFruticStore from '../../store/useFruticStore';
import useWarningsStore from '../../store/warningsStore';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { VALID_FORMATS } from 'common/src/utils/definitions';
import useSettlementsStore from '../../store/useSettlementsStore';
import useExporterLabelsStore from '../../store/useExporterLabelsStore';
import { LoadingState } from '../ui';
import PackingCodeMappingEditor from 'common/src/components/shipments/PackingCodeMappingEditor';

export const BoxFormatMappingSection = ({
  speciesId,
  season,
  exporterRef,
  exporterId,
  hide,
  compact = false,
  embedded = false,
  description,
  defaultExpanded = false,
}: {
  speciesId: number;
  season: string;
  exporterRef?: string;
  exporterId?: number;
  hide?: boolean;
  compact?: boolean;
  embedded?: boolean;
  description?: string;
  defaultExpanded?: boolean;
}) => {
  const { t } = useTranslation('preSettlements');
  const { userProfile, session } = useUserStore();
  const mappingExporterId = exporterId ?? userProfile?.exporter_id;
  const {
    mappedFormats,
    unmappedFormats,
    fetchMappings,
    updateMapping,
    deleteMapping,
    loading,
  } = useFruticStore();
  const { uniqueLabels } = useSettlementsStore();
  const { setWarnings } = useWarningsStore();
  const [isOpen, setIsOpen] = useState(true);

  const { labels, fetchLabels } = useExporterLabelsStore();
  const [hasInitializedOpenState, setHasInitializedOpenState] = useState(false);

  // Default to closed when all formats are mapped, open when there are unmapped formats
  useEffect(() => {
    if (!loading && !hasInitializedOpenState) {
      setIsOpen(defaultExpanded || unmappedFormats.length > 0);
      setHasInitializedOpenState(true);
    }
  }, [
    defaultExpanded,
    loading,
    unmappedFormats.length,
    hasInitializedOpenState,
  ]);

  const officialLabels = useMemo(() => {
    const labelList = labels
      .map((item) => item.label?.trim().toUpperCase())
      .filter((label) => label.length > 0);
    return Array.from(new Set(labelList)).sort((a, b) => a.localeCompare(b));
  }, [labels]);

  // useEffect(() => {
  //   if (userProfile && session) {
  //     fetchMappings(speciesId, season, session, exporterRef, userProfile);
  //   }
  // }, [speciesId, season, exporterRef, fetchMappings]);

  useEffect(() => {
    if (!mappingExporterId || !speciesId || !season) return;
    fetchLabels(mappingExporterId, speciesId, season).catch((error) => {
      toast.error('Error al cargar las etiquetas.');
      console.error(error);
    });
  }, [fetchLabels, season, speciesId, mappingExporterId]);

  useEffect(() => {
    const normalizeValue = (value: string | null | undefined) =>
      (value || '').trim().toUpperCase();

    const missingCodes = new Set<string>();
    unmappedFormats.forEach((code) => {
      const normalized = normalizeValue(code);
      if (normalized) missingCodes.add(normalized);
    });
    mappedFormats.forEach((mapping) => {
      const code = normalizeValue(mapping.frutic_code);
      const format = normalizeValue(mapping.cherry_box_format || '');
      const missingFormat = !format || format === 'NA';
      if (code && missingFormat) {
        missingCodes.add(code);
      }
    });

    const officialLabelSet = new Set(officialLabels.map(normalizeValue));

    const settlementLabelsMissingInMappings =
      officialLabelSet.size > 0
        ? Array.from(
            new Set(
              uniqueLabels
                .map((label) => normalizeValue(label))
                .filter(
                  (label) => label.length > 0 && !officialLabelSet.has(label)
                )
            )
          ).sort((a, b) => a.localeCompare(b))
        : [];

    setWarnings({
      codesMissingMapping: Array.from(missingCodes).sort((a, b) =>
        a.localeCompare(b)
      ),
      settlementLabelsMissingInMappings,
    });
  }, [
    mappedFormats,
    officialLabels,
    setWarnings,
    uniqueLabels,
    unmappedFormats,
  ]);

  if (hide) return null;

  if (loading) return <LoadingState />;

  const content = (
    <>
      <div className={isOpen ? 'mb-4' : ''}>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <div className='flex flex-1 flex-wrap items-center gap-x-4 gap-y-2'>
            <p className='text-md font-bold text-default-600'>
              {t('Mapeo de Códigos de Embalaje')}
            </p>
            {unmappedFormats.length > 0 ? (
              <div className='flex items-center gap-2'>
                <span className='inline-flex items-center rounded-full bg-yellow-100 dark:bg-yellow-900 px-2.5 py-0.5 text-xs font-medium text-yellow-800 dark:text-yellow-200'>
                  {unmappedFormats.length} {t('sin mapear')}
                </span>
                <span className='inline-flex items-center rounded-full bg-green-100 dark:bg-green-900 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:text-green-200'>
                  {mappedFormats.length} {t('mapeadas')}
                </span>
              </div>
            ) : (
              <span className='inline-flex items-center rounded-full bg-green-100 dark:bg-green-900 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:text-green-200'>
                {t('Todas mapeadas')}
              </span>
            )}
            {compact && (
              <p className='text-xs text-default-500'>
                {description ||
                  t(
                    'Indica el formato que corresponde a cada código de embalaje de la Base de Despachos'
                  )}
              </p>
            )}
          </div>
          <Button
            size='sm'
            variant='light'
            onClick={() => setIsOpen(!isOpen)}
            className='min-w-[100px] px-4 py-2 text-sm font-medium text-primary-500'
          >
            {isOpen
              ? t('Ocultar Mapeo')
              : compact
                ? t('Ver mapeo')
                : t('Mostrar Mapeo')}
          </Button>
        </div>
        {!compact && (
          <p className='text-xs text-default-400'>
            {t(
              'Indica el formato que corresponde a cada código de embalaje de la Base de Despachos'
            )}
          </p>
        )}
      </div>

      <div></div>

      {isOpen && (
        <>
          <div className='mb-4'>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
              {mappedFormats.map((mapping) => {
                return (
                  <div
                    key={mapping.id}
                    className='group relative rounded-medium border border-default-200 p-4'
                  >
                    <div className='flex items-center justify-between mb-3'>
                      <div className='flex items-center gap-2'>
                        <span className='text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400'>
                          Código Embalaje
                        </span>
                        <span className='inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs font-semibold text-slate-700 dark:text-slate-300'>
                          {mapping.frutic_code}
                        </span>
                      </div>
                      <button
                        onClick={() =>
                          deleteMapping(mapping.id!, mapping.frutic_code)
                        }
                        className='rounded-md p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400'
                        aria-label={t('Eliminar mapeo')}
                      >
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          className='h-5 w-5'
                          viewBox='0 0 20 20'
                          fill='currentColor'
                        >
                          <path
                            fillRule='evenodd'
                            d='M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z'
                            clipRule='evenodd'
                          />
                        </svg>
                      </button>
                    </div>
                    <div className='grid grid-cols-1 gap-3'>
                      <div className='flex flex-col'>
                        <label
                          className='text-xs mb-1 text-slate-600 dark:text-slate-400'
                          htmlFor={`format-select-${mapping.id}`}
                        >
                          Formato
                        </label>
                        <Select
                          id={`format-select-${mapping.id}`}
                          size='sm'
                          className='w-full'
                          selectedKeys={[mapping.cherry_box_format]}
                          onChange={(e) =>
                            updateMapping(
                              mapping.id!,
                              e.target.value as (typeof VALID_FORMATS)[number]
                            )
                          }
                        >
                          {VALID_FORMATS.map((format) => (
                            <SelectItem key={format} value={format}>
                              {format}
                            </SelectItem>
                          ))}
                        </Select>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {unmappedFormats.length > 0 && mappingExporterId && (
            <div className='mt-4'>
              <PackingCodeMappingEditor
                key={mappingExporterId}
                exporterId={mappingExporterId}
                codes={unmappedFormats}
                onMapped={(mapping) => {
                  const current = useFruticStore.getState();
                  current.setMappedAndUnmappedFormats(
                    [...current.mappedFormats.filter((item) => item.frutic_code !== mapping.frutic_code), mapping],
                    current.unmappedFormats.filter((code) => code !== mapping.frutic_code)
                  );
                }}
              />
            </div>
          )}

          {/* <div className='mt-2'>
              <Button
                color='primary'
                onClick={() => onMappingChange()}
                className='w-[200px]'
              >
                Confirmar Cambios
              </Button>
            </div> */}
        </>
      )}
    </>
  );

  return compact && !embedded ? (
    <Card shadow='sm'>
      <CardBody className='p-6'>{content}</CardBody>
    </Card>
  ) : (
    <div className={embedded ? 'px-5 py-4 md:px-7' : 'mb-4'}>{content}</div>
  );
};
